import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { collectPaginatedRows } from "../lib/paginatedRows";
import { selectHistoricalYield, type HistoricalYieldObservation, type HistoricalYieldSelection } from "../domain/historicalYieldSelection";

type Props = {
  workCode: string;
  canUse: boolean;
  onSelect: (selection: HistoricalYieldSelection) => void;
  onReview: () => void;
};

export default function HistoricalYieldReferences({ workCode, canUse, onSelect, onReview }: Props) {
  const [rows, setRows] = useState<HistoricalYieldObservation[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    setBusy(true); setError(""); setRows([]);
    void collectPaginatedRows<HistoricalYieldObservation>((from, to) =>
      supabase.from("historical_yield_review").select("*")
        .or("mapped_work_code.eq." + workCode + ",suggested_work_code.eq." + workCode)
        .neq("review_status", "rejected")
        .order("source_year", { ascending: false }).order("id").range(from, to),
    ).then((result) => {
      if (cancelled) return;
      if (result.error) setError(result.error.message);
      else setRows(result.data ?? []);
      setBusy(false);
    });
    return () => { cancelled = true; };
  }, [workCode]);

  function apply(row: HistoricalYieldObservation) {
    if (!canUse) return;
    try { onSelect(selectHistoricalYield(row, workCode)); setError(""); }
    catch (problem) { setError(problem instanceof Error ? problem.message : String(problem)); }
  }

  return <section className="card engine-parameters">
    <div className="engine-section-head"><div>
      <p className="eyebrow">Consumo histórico de aço</p><h3>Referências para esta unidade</h3>
    </div><button className="button button-secondary" type="button" onClick={onReview}>Rever rendimentos</button></div>
    <p className="muted">Uma ligação revista permite aplicar o consumo apenas neste cálculo. O tipo de aço e o desperdício continuam a ser definidos separadamente.</p>
    {error ? <div className="alert alert-error">{error}</div> : null}
    {busy ? <div className="empty">A carregar referências…</div> : null}
    {!busy && !error && rows.length === 0 ? <p className="muted">Ainda não existem rendimentos ligados ou sugestões inequívocas para esta unidade. Reveja as notas antes de escolher um valor.</p> : null}
    <div className="historical-list">{rows.map((row) => {
      const eligible = row.review_status === "mapped" && row.mapped_work_code === workCode;
      return <article className="historical-candidate-card" key={row.id}>
        <div className="historical-candidate-main">
          <div className="result-meta"><span>{row.source_year}</span><span>{eligible ? "Ligação revista" : "Sugestão por rever"}</span></div>
          <h4>{Number(row.quantity).toLocaleString("pt-PT")} kg/m³ · {row.original_description}</h4>
          <p className="historical-evidence">{row.evidence}</p>
          <small className="muted">{row.source_file.split("/").pop()} · {row.sheet_name} · {row.cell_ref}</small>
          <details><summary>Nota original</summary><p style={{ whiteSpace: "pre-wrap" }}>{row.raw_comment || row.evidence}</p></details>
        </div>
        <div className="historical-candidate-actions">
          {eligible && canUse ? <button className="button button-primary" type="button" onClick={() => apply(row)}>Aplicar neste cálculo</button> : null}
          {!eligible ? <small className="muted">Confirmar primeiro a ligação no separador Rendimentos.</small> : null}
        </div>
      </article>;
    })}</div>
  </section>;
}
